import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";
import {
    ArrowLeft, Mail, Calendar, Clock, MessageSquare, Gauge, BookOpen,
    Shield, CheckCircle2, XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/Skeleton";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { Sparkline } from "@/components/Sparkline";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { cn } from "@/lib/utils";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return format(new Date(iso), "d MMM yyyy"); } catch { return iso; }
};
const fmtRel = (iso) => {
    if (!iso) return "never";
    try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return iso; }
};

const MetricCard = ({ icon: Icon, label, value, sublabel, testid }) => (
    <Card className="border-border/80 shadow-sm" data-testid={testid}>
        <CardContent className="p-5 space-y-1.5">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Icon className="h-3.5 w-3.5 text-primary" />
                {label}
            </div>
            <p className="text-2xl font-semibold tabular-nums tracking-tight text-foreground">{value}</p>
            {sublabel && <p className="text-xs text-muted-foreground">{sublabel}</p>}
        </CardContent>
    </Card>
);

export default function AdminUserDetailPage() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    useDocumentTitle("Admin · User");

    useEffect(() => {
        let cancelled = false;
        setData(null);
        api.get(`/admin/users/${id}`)
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch((e) => {
                if (cancelled) return;
                const status = e?.response?.status;
                if (status === 404) setError("User not found.");
                else setError(e?.response?.data?.detail || "Failed to load user");
            });
        return () => { cancelled = true; };
    }, [id]);

    return (
        <Layout>
            <div className="space-y-8 animate-fade-in-up" data-testid="admin-user-detail-page">
                <Button asChild variant="ghost" size="sm" className="-ml-2 text-muted-foreground" data-testid="admin-user-back">
                    <Link to="/admin/users">
                        <ArrowLeft className="h-4 w-4" />
                        All users
                    </Link>
                </Button>

                {error && (
                    <Card className="border-destructive/50 bg-destructive/5">
                        <CardContent className="p-4 text-sm text-destructive" data-testid="admin-user-error">{error}</CardContent>
                    </Card>
                )}

                {!data && !error ? (
                    <div className="space-y-6">
                        <Skeleton className="h-24 w-full rounded-2xl" />
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
                        </div>
                    </div>
                ) : data && (
                    <>
                        {/* Header */}
                        <Card className="border-border/80 shadow-sm" data-testid="admin-user-header">
                            <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center">
                                <InitialsAvatar name={data.full_name} className="h-16 w-16 text-xl" dataTestId="admin-user-avatar" />
                                <div className="min-w-0 flex-1 space-y-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h1 className="text-2xl font-semibold tracking-tight text-foreground" data-testid="admin-user-name">
                                            {data.full_name || "—"}
                                        </h1>
                                        <span
                                            className={cn(
                                                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium",
                                                data.role === "admin"
                                                    ? "border-primary/40 bg-primary/10 text-primary"
                                                    : "border-border bg-secondary text-muted-foreground"
                                            )}
                                            data-testid="admin-user-role-pill"
                                        >
                                            {data.role === "admin" && <Shield className="h-3 w-3" />}
                                            {data.role}
                                        </span>
                                    </div>
                                    <p className="text-sm text-muted-foreground" data-testid="admin-user-email">
                                        {data.email}
                                    </p>
                                    <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-xs text-muted-foreground">
                                        <span className="inline-flex items-center gap-1">
                                            <Calendar className="h-3 w-3" />
                                            Member since {fmtDate(data.created_at)}
                                        </span>
                                        <span className="inline-flex items-center gap-1" data-testid="admin-user-last-login">
                                            <Clock className="h-3 w-3" />
                                            Last login {fmtRel(data.last_login_at)}
                                        </span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* 4 stat cards */}
                        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-user-stats">
                            <MetricCard
                                icon={MessageSquare}
                                label="Sessions"
                                value={data.stats.total_sessions}
                                sublabel={`${data.stats.total_reviewed_sessions} reviewed`}
                                testid="admin-user-stat-sessions"
                            />
                            <MetricCard
                                icon={Clock}
                                label="Hours practiced"
                                value={`${data.stats.total_hours_practiced.toFixed(1)}h`}
                                sublabel={data.stats.skill_level}
                                testid="admin-user-stat-hours"
                            />
                            <MetricCard
                                icon={Gauge}
                                label="Avg score"
                                value={data.stats.average_score == null ? "—" : Math.round(data.stats.average_score)}
                                sublabel="Across reviewed sessions"
                                testid="admin-user-stat-avg"
                            />
                            <MetricCard
                                icon={BookOpen}
                                label="Learn progress"
                                value={`${data.learn.chapters_completed}/${data.learn.total_chapters}`}
                                sublabel={data.learn.average_quiz_score == null
                                    ? "No quizzes yet"
                                    : `Avg quiz ${Math.round(data.learn.average_quiz_score)}%`}
                                testid="admin-user-stat-learn"
                            />
                        </section>

                        {/* Sparkline + Therapy type breakdown */}
                        <section className="grid gap-6 lg:grid-cols-3">
                            <Card className="border-border/80 shadow-sm lg:col-span-2" data-testid="admin-user-sparkline-card">
                                <CardContent className="p-5 space-y-3">
                                    <h2 className="text-base font-semibold text-foreground">Score trend</h2>
                                    <p className="text-xs text-muted-foreground">Last {data.stats.recent_scores.length} reviewed sessions</p>
                                    <Sparkline values={data.stats.recent_scores} dataTestId="admin-user-sparkline" />
                                </CardContent>
                            </Card>
                            <Card className="border-border/80 shadow-sm" data-testid="admin-user-therapy-types">
                                <CardContent className="p-5 space-y-3">
                                    <h2 className="text-base font-semibold text-foreground">Practice by type</h2>
                                    {data.stats.by_therapy_type.length === 0 ? (
                                        <p className="text-xs text-muted-foreground">No sessions yet.</p>
                                    ) : (
                                        <ul className="space-y-2">
                                            {data.stats.by_therapy_type.map((t) => (
                                                <li key={t.therapy_type_slug} className="space-y-1" data-testid={`admin-user-tt-${t.therapy_type_slug}`}>
                                                    <div className="flex items-baseline justify-between text-xs">
                                                        <span className="font-medium text-foreground">{t.therapy_type_name}</span>
                                                        <span className="text-muted-foreground tabular-nums">
                                                            {t.sessions}
                                                            {t.average_score != null && (
                                                                <span className="ml-2">avg {Math.round(t.average_score)}</span>
                                                            )}
                                                        </span>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </CardContent>
                            </Card>
                        </section>

                        {/* Learn by module */}
                        <Card className="border-border/80 shadow-sm" data-testid="admin-user-learn-modules">
                            <CardContent className="p-5 space-y-4">
                                <h2 className="text-base font-semibold text-foreground">Learn progress by method</h2>
                                <ul className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                                    {data.learn.by_module.map((m) => {
                                        const pct = m.chapter_count
                                            ? Math.round((m.chapters_completed / m.chapter_count) * 100)
                                            : 0;
                                        return (
                                            <li key={m.module_slug} className="space-y-1" data-testid={`admin-user-mod-${m.module_slug}`}>
                                                <div className="flex items-baseline justify-between text-xs">
                                                    <span className="font-medium text-foreground">{m.name}</span>
                                                    <span className="text-muted-foreground tabular-nums">
                                                        {m.chapters_completed}/{m.chapter_count}
                                                    </span>
                                                </div>
                                                <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                                                    <div
                                                        className="h-full rounded-full bg-primary transition-all duration-700"
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </CardContent>
                        </Card>

                        {/* Recent sessions */}
                        <Card className="border-border/80 shadow-sm" data-testid="admin-user-recent-sessions">
                            <CardContent className="p-5">
                                <div className="flex items-baseline justify-between mb-3">
                                    <h2 className="text-base font-semibold text-foreground">Recent sessions</h2>
                                    <span className="text-xs text-muted-foreground">10 most recent</span>
                                </div>
                                {data.recent_sessions.length === 0 ? (
                                    <p className="py-6 text-center text-sm text-muted-foreground">No sessions yet.</p>
                                ) : (
                                    <ul className="divide-y divide-border/70">
                                        {data.recent_sessions.map((s) => {
                                            const isReviewed = s.status === "reviewed" && s.overall_score != null;
                                            const content = (
                                                <div className="flex items-center justify-between gap-3 py-3">
                                                    <div className="min-w-0 flex-1">
                                                        <p className="truncate text-sm font-medium text-foreground">
                                                            {s.avatar_name} · {s.case_title}
                                                        </p>
                                                        <p className="truncate text-xs text-muted-foreground">
                                                            {s.therapy_type_name}
                                                            <span className="mx-1.5 text-border">·</span>
                                                            {fmtRel(s.started_at || s.ended_at)}
                                                        </p>
                                                    </div>
                                                    <div className="flex shrink-0 items-center gap-3">
                                                        {isReviewed ? (
                                                            <span
                                                                className={cn(
                                                                    "rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
                                                                    s.overall_score >= 70 ? "bg-emerald-100 text-emerald-700"
                                                                        : s.overall_score >= 50 ? "bg-amber-100 text-amber-700"
                                                                            : "bg-rose-100 text-rose-700"
                                                                )}
                                                            >
                                                                {s.overall_score}
                                                            </span>
                                                        ) : (
                                                            <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground capitalize">
                                                                {s.status}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                            return (
                                                <li key={s.id} data-testid={`admin-user-session-${s.id}`}>
                                                    {isReviewed ? (
                                                        <Link to={`/practice/review/${s.id}`} className="block hover:bg-secondary/40 px-2 -mx-2 rounded transition-colors">
                                                            {content}
                                                        </Link>
                                                    ) : (
                                                        <div className="px-2 -mx-2">{content}</div>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </CardContent>
                        </Card>

                        {/* Recent learn attempts */}
                        <Card className="border-border/80 shadow-sm" data-testid="admin-user-recent-learn">
                            <CardContent className="p-5">
                                <div className="flex items-baseline justify-between mb-3">
                                    <h2 className="text-base font-semibold text-foreground">Recent learn attempts</h2>
                                    <span className="text-xs text-muted-foreground">10 most recent</span>
                                </div>
                                {data.recent_learn_attempts.length === 0 ? (
                                    <p className="py-6 text-center text-sm text-muted-foreground">No quiz attempts yet.</p>
                                ) : (
                                    <ul className="divide-y divide-border/70">
                                        {data.recent_learn_attempts.map((a, i) => (
                                            <li
                                                key={`${a.chapter_slug}-${i}`}
                                                className="flex items-center justify-between gap-3 py-3"
                                                data-testid={`admin-user-attempt-${i}`}
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <p className="truncate text-sm font-medium text-foreground">{a.chapter_title}</p>
                                                    <p className="truncate text-xs text-muted-foreground">
                                                        {fmtRel(a.attempted_at)} · {a.score}/{a.total}
                                                    </p>
                                                </div>
                                                <span
                                                    className={cn(
                                                        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
                                                        a.passed ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                                                    )}
                                                >
                                                    {a.passed ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                                    {a.percentage}%
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </CardContent>
                        </Card>

                        {/* Contact line at bottom */}
                        <div className="pt-2 text-xs text-muted-foreground" data-testid="admin-user-contact">
                            <span className="inline-flex items-center gap-1.5">
                                <Mail className="h-3 w-3" />
                                Contact:{" "}
                                <span className="select-all font-mono text-foreground" data-testid="admin-user-contact-email">
                                    {data.email}
                                </span>
                            </span>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
}
